/**
 * S4 Industry Pack catalog table. Presentational — filtering stays in the host page.
 */

import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { LifecycleBadge } from '@/components/ai-studio/agents';
import type { AiStudioIndustryPack } from '@/types/aiStudioAgent';

interface PackTableProps {
  packs: AiStudioIndustryPack[];
  onSelect: (packId: string) => void;
}

const HEADER_CLASS =
  'px-3 py-2 text-left text-base font-medium text-[var(--color-surface-text-secondary)]';
const CELL_CLASS = 'px-3 py-2 text-base text-[var(--color-surface-text)]';

export const PackTable: React.FC<PackTableProps> = ({ packs, onSelect }) => {
  const { t } = useLanguage();

  return (
    <table className="w-full border-collapse">
      <thead>
        <tr className="border-b border-[var(--color-border)]">
          <th className={HEADER_CLASS}>{t('aiStudio.packs.columnCode')}</th>
          <th className={HEADER_CLASS}>{t('aiStudio.packs.columnName')}</th>
          <th className={HEADER_CLASS}>{t('aiStudio.packs.columnDomain')}</th>
          <th className={HEADER_CLASS}>{t('aiStudio.packs.columnScope')}</th>
          <th className={HEADER_CLASS}>{t('aiStudio.packs.columnStatus')}</th>
          <th className={HEADER_CLASS}>{t('aiStudio.packs.columnUpdated')}</th>
        </tr>
      </thead>
      <tbody>
        {packs.map((pack) => (
          <tr
            key={pack.id}
            tabIndex={0}
            role="link"
            onClick={() => onSelect(pack.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelect(pack.id);
              }
            }}
            className="cursor-pointer border-b border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
          >
            <td className={`${CELL_CLASS} font-mono`}>{pack.code}</td>
            <td className={`${CELL_CLASS} font-medium`}>{pack.name}</td>
            <td className={CELL_CLASS}>{pack.domain || t('aiStudio.common.notSet')}</td>
            <td className={CELL_CLASS}>
              {pack.tenantId === null
                ? t('aiStudio.packs.scopePlatform')
                : t('aiStudio.packs.scopeTenant')}
            </td>
            <td className={CELL_CLASS}>
              <LifecycleBadge status={pack.lifecycleStatus} />
            </td>
            <td className={CELL_CLASS}>{new Date(pack.updatedAt).toLocaleDateString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
